/**
 * sw-registration — registro do Service Worker + fluxo de atualização (Story X1.1.2).
 *
 * Encapsula `workbox-window` para:
 *   1. Registrar `/service-worker.js` (mesma URL que usePushSubscription espera).
 *   2. Detectar quando uma versão NOVA está em "waiting" (deploy publicado) e
 *      avisar a UI via pub/sub — o `UpdatePrompt` mostra "Nova versão disponível".
 *   3. Aplicar a atualização quando o usuário confirma: envia SKIP_WAITING ao SW
 *      (o handler em service-worker.ts chama self.skipWaiting()) e, quando o SW
 *      novo assume o controle (`controlling`), recarrega a página uma única vez.
 *
 * Estado de módulo (singleton) porque há um único SW por origem. Mantemos a
 * lógica fina e testável: as funções são puras quanto a efeitos observáveis
 * (pub/sub + reload), e `workbox-window` é mockável nos testes.
 */
import { Workbox } from "workbox-window";

const SW_URL = "/service-worker.js";

type UpdateListener = (updateReady: boolean) => void;

const listeners = new Set<UpdateListener>();
let wb: Workbox | null = null;
let updateReady = false;
let skipRequested = false;
let reloaded = false;

function notify(): void {
  for (const listener of listeners) listener(updateReady);
}

/** SW + workbox-window utilizáveis neste navegador/contexto. */
function isSupported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

/**
 * Assina mudanças no estado "update disponível". Emite o valor atual na hora da
 * inscrição (para componentes que montam depois do evento `waiting`). Devolve uma
 * função de cleanup.
 */
export function subscribeToUpdate(listener: UpdateListener): () => void {
  listeners.add(listener);
  listener(updateReady);
  return () => {
    listeners.delete(listener);
  };
}

/** Estado atual — útil para leitura síncrona/inicialização de hooks. */
export function isUpdateReady(): boolean {
  return updateReady;
}

/**
 * Registra o Service Worker e arma a detecção de atualização. Idempotente: chamar
 * mais de uma vez não cria registros duplicados. No-op se SW não for suportado
 * (ex.: contexto não-seguro HTTP/IP — limitação conhecida do dev do projeto).
 */
export function registerServiceWorker(): void {
  if (!isSupported() || wb) return;

  wb = new Workbox(SW_URL);

  const handleWaiting = (): void => {
    updateReady = true;
    notify();
  };

  // `waiting`: SW novo instalado, aguardando ativação. No workbox-window v7 este
  // mesmo evento cobre o caso "externo" (outra aba detectou o SW novo) — vem com
  // a flag `isExternal: true`. Tratamos os dois de forma idêntica: mostrar o prompt.
  wb.addEventListener("waiting", handleWaiting);

  // `controlling`: o SW novo assumiu o controle. Só recarregamos se a troca
  // partiu de um SKIP_WAITING que NÓS pedimos (evita reload em 1ª instalação).
  wb.addEventListener("controlling", () => {
    if (skipRequested) reloadOnce();
  });

  wb.register().catch((err: unknown) => {
    // Fora do critical path: falha aqui não impede o app de bootar.
    console.warn("[SW] registration failed:", err);
  });
}

/** Margem de segurança (ms) para o reload de fallback do applyUpdate. */
const APPLY_UPDATE_RELOAD_TIMEOUT = 2000;

function reloadOnce(): void {
  if (reloaded || typeof window === "undefined") return;
  reloaded = true;
  window.location.reload();
}

/**
 * Aplica a atualização pendente: pede ao SW em espera para assumir (SKIP_WAITING).
 * O caminho feliz recarrega via listener `controlling`. Como rede de segurança,
 * agendamos um reload com timeout: se o SW em espera já tiver sido ativado por
 * outra aba, `messageSkipWaiting()` é no-op e `controlling` NÃO dispara — sem o
 * fallback o botão "Atualizar" ficaria travado em "Atualizando…" pra sempre.
 * O guard `reloaded` garante recarregar uma única vez (o que vier primeiro).
 * No-op se não houver update pronto.
 */
export function applyUpdate(): void {
  if (!wb || !updateReady) return;
  skipRequested = true;
  wb.messageSkipWaiting();
  if (typeof window !== "undefined") {
    window.setTimeout(reloadOnce, APPLY_UPDATE_RELOAD_TIMEOUT);
  }
}

/** Reseta o estado do módulo. APENAS para testes. */
export function __resetSwRegistrationForTests(): void {
  listeners.clear();
  wb = null;
  updateReady = false;
  skipRequested = false;
  reloaded = false;
}
